// Browser side effects for consent. Every function takes its globals so tests use doubles.
import {
  CONSENT_DEFAULTS,
  CONSENT_STORAGE_KEY,
  CONSENT_UPDATE_EVENT,
  GTM_ID,
  GA4_DISABLE_KEY,
  parseStoredConsent,
  OPEN_CONSENT_EVENT,
  type ConsentChoice,
  makeChoice,
  isProductionHost,
  optionalChanged as optionalChangedFrom,
  optionalCookieNames,
  shouldLoadGtm,
  toGoogleConsent,
} from "./consent";
import { clearAttribution, attributionChoiceValid } from "./agency-attribution";

type Win = {
  [GA4_DISABLE_KEY]?: boolean;
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  __seoevoGtmLoaded?: boolean;
  /** shared with the inline head bootstrap: defaults already queued */
  __seoevoConsentInit?: boolean;
  /** shared with the inline head bootstrap: last consent state queued as update ("analytics|ads") */
  __seoevoConsentApplied?: string;
};
type Doc = {
  cookie: string;
  head: { appendChild: (el: { async?: boolean; src?: string; id?: string }) => unknown };
  createElement: (tag: string) => { async?: boolean; src?: string; id?: string };
  getElementById: (id: string) => unknown;
};

function gtagOf(win: Win) {
  win.dataLayer = win.dataLayer || [];
  if (!win.gtag) {
    win.gtag = function () {
      // eslint-disable-next-line prefer-rest-params -- gtag needs the Arguments object
      win.dataLayer?.push(arguments);
    };
  }
  return win.gtag;
}

/**
 * Idempotent runtime init: queues Consent Mode defaults (all denied) once, even if the inline head
 * script was stripped. The marker is shared with the inline script so late defaults never overwrite an update.
 */
export function ensureConsentDefaults(win: Win): boolean {
  if (win.__seoevoConsentInit) return false;
  win[GA4_DISABLE_KEY] = true;
  win.__seoevoConsentInit = true;
  const g = gtagOf(win);
  g("consent", "default", { ...CONSENT_DEFAULTS, wait_for_update: 500 });
  g("set", "ads_data_redaction", true);
  g("set", "url_passthrough", false);
  return true;
}

export function applyConsentUpdate(win: Win, choice: ConsentChoice | null) {
  const valid = parseStoredConsent(JSON.stringify(choice), Date.now());
  // SDK opt-out must precede every gtag call, including defaults on a bare page.
  win[GA4_DISABLE_KEY] = valid?.analytics !== true;
  ensureConsentDefaults(win);
  win[GA4_DISABLE_KEY] = valid?.analytics !== true;
  const state = toGoogleConsent(valid);
  const key = `${state.analytics_storage}|${state.ad_storage}`;
  if (win.__seoevoConsentApplied === key) return; // same state already queued
  win.__seoevoConsentApplied = key;
  gtagOf(win)("consent", "update", state);
  win.dataLayer?.push({
    event: CONSENT_UPDATE_EVENT,
    consent_analytics: state.analytics_storage,
    consent_ads: state.ad_storage,
  });
}

export type GtmLoadResult = "loaded" | "already" | "blocked" | "qa-skipped";

/**
 * Injects the GTM container at most once, only on the production host with an optional category granted.
 * Order is guaranteed here, independent of the inline head script: default → update → gtm.start → script.
 */
export function ensureGtmLoaded(
  win: Win,
  doc: Doc,
  host: string,
  choice: ConsentChoice | null,
  opts: { qa?: boolean; log?: (msg: string) => void } = {},
): GtmLoadResult {
  choice = parseStoredConsent(JSON.stringify(choice), Date.now());
  if (win.__seoevoGtmLoaded || doc.getElementById("seoevo-gtm")) return "already";
  if (!isProductionHost(host)) {
    if (opts.qa && choice && (choice.analytics || choice.ads)) {
      // Same queue order as production, so QA can inspect it; no script is ever injected here.
      ensureConsentDefaults(win);
      applyConsentUpdate(win, choice);
      opts.log?.(`[consent-qa] GTM ${GTM_ID} se cargaría en producción; no se carga en ${host}`);
      return "qa-skipped";
    }
    return "blocked";
  }
  if (!choice || !shouldLoadGtm(choice, host)) return "blocked";
  const initialized = ensureConsentDefaults(win);
  applyConsentUpdate(win, choice);
  // A pre-existing bootstrap/other script may have changed this setting. Reassert before injection.
  if (!initialized) gtagOf(win)("set", "url_passthrough", false);
  win.__seoevoGtmLoaded = true;
  win.dataLayer?.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const s = doc.createElement("script");
  s.id = "seoevo-gtm";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  doc.head.appendChild(s);
  return "loaded";
}

/** Expires first-party optional cookies on this host and the parent domain. Returns names cleared. */
export function clearOptionalCookies(doc: Doc, host: string, choice: ConsentChoice): string[] {
  const names = optionalCookieNames(doc.cookie, choice);
  const domains = ["", host, isProductionHost(host) ? ".agenciaseoevolution.com" : ""].filter(
    (d, i, a) => a.indexOf(d) === i,
  );
  for (const n of names) {
    for (const d of domains) {
      doc.cookie = `${n}=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ""}`;
    }
  }
  return names;
}

/** Saves and reads back; returns false when storage is unavailable or did not keep the value. */
export function saveChoice(
  storage: { setItem: (k: string, v: string) => void; getItem: (k: string) => string | null },
  choice: ConsentChoice,
): boolean {
  try {
    const raw = JSON.stringify(choice);
    storage.setItem(CONSENT_STORAGE_KEY, raw);
    return storage.getItem(CONSENT_STORAGE_KEY) === raw;
  } catch {
    return false;
  }
}

export function openConsentPreferences() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(OPEN_CONSENT_EVENT));
}

// In-memory current choice shared with the analytics tracker (null until the user decides).
export const CONSENT_CHANGED_EVENT = "seoevo:consent-changed";
let currentChoice: ConsentChoice | null = null;
export function getCurrentChoice() {
  return currentChoice;
}
export function setCurrentChoice(c: ConsentChoice | null) {
  currentChoice = c;
  if (typeof window !== "undefined" && !attributionChoiceValid(c, Date.now())) {
    try { clearAttribution(window.localStorage); } catch { /* storage blocked */ }
  }
  if (typeof window !== "undefined") window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
}

/** Stop emission and update Google BEFORE cleanup or any navigation/unload. Also used at mount. */
export function applyRuntimeConsent(win: Win, doc: Doc, host: string, next: ConsentChoice | null) {
  next = parseStoredConsent(JSON.stringify(next), Date.now());
  win[GA4_DISABLE_KEY] = next?.analytics !== true;
  // Revocation must stop the tracker synchronously; grants are announced only after Google's update.
  if (!next?.analytics) setCurrentChoice(next);
  applyConsentUpdate(win, next);
  if (next?.analytics) setCurrentChoice(next);
  clearOptionalCookies(doc, host, next ?? makeChoice(false, false, Date.now()));
}

/**
 * Applies a consent change made in another tab. A missing/invalid/expired record is treated as a
 * revocation: denied update, runtime choice null at once, optional cookies cleared, clean reload if GTM
 * already ran. The record stays absent so the banner asks again; after reload GTM is not loaded → no loop.
 */
export function applyExternalConsent(
  deps: {
    win: Win;
    doc: Doc;
    host: string;
    gtmLoaded: boolean;
    reload: () => void;
    load: (c: ConsentChoice) => void;
  },
  prev: ConsentChoice | null,
  next: ConsentChoice | null,
): "reload" | "applied" | "revoked" {
  applyRuntimeConsent(deps.win, deps.doc, deps.host, next);
  if (!next) {
    if (deps.gtmLoaded) {
      deps.reload();
      return "reload";
    }
    return "revoked";
  }
  if (deps.gtmLoaded && optionalChangedFrom(prev, next)) {
    deps.reload();
    return "reload";
  }
  deps.load(next);
  return "applied";
}
